<script setup lang="ts">
import {computed} from "vue";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import LegacyAccess from "@/core/interfaces/Access";
import {TeamTask} from "@/core/interfaces/TeamTask";
import {initialsOf, nextStatus, previousStatus, priorityOption} from "@/team-task/taskBoard";
import {ChevronLeft, ChevronRight, GripVertical, MessageSquare, UserPlus} from "@lucide/vue";

const {task, members, draggable = true} = defineProps<{
    task: TeamTask;
    /** Team roster, to resolve the assignee names and initials. */
    members: LegacyAccess[];
    draggable?: boolean;
}>();

const emits = defineEmits<{
    (event: 'open'): void;
    (event: 'claim'): void;
    (event: 'advance'): void;
    (event: 'recede'): void;
    (event: 'grab', payload: PointerEvent): void;
}>();

const priority = computed(() => priorityOption(task.priority));

const assignees = computed(() =>
    task.assignee_ids
        .map(id => members.find(member => member.id === id))
        .filter((member): member is LegacyAccess => !!member)
);

const assigneeLabel = computed(() => {
    if (assignees.value.length === 1) {
        return assignees.value[0].user?.name ?? '';
    }

    return `${assignees.value.length} personnes`;
});

/** "2 sept", as the rendered mockup dates a card. */
const dueLabel = computed(() =>
    task.due_date ? dayjs(task.due_date).locale('fr').format('D MMM').replace('.', '') : null
);

const canAdvance = computed(() => !!nextStatus(task.status));
const canRecede = computed(() => !!previousStatus(task.status));
</script>

<template>
  <article
    class="group rounded-xl border border-gray-200 bg-white p-2.5 shadow-sm transition hover:border-primary/40 hover:shadow"
    @click="emits('open')"
  >
    <div class="flex items-start gap-1.5">
      <!--
        `touch-none` is what makes the drag possible at all on a touch screen:
        without it the browser claims the gesture for panning and answers with
        a pointercancel. And the grip stays visible instead of appearing on
        hover — hover never happens on touch, and even on a mouse a hidden
        handle keeps the board from ever looking draggable.
      -->
      <button
        v-if="draggable"
        class="-m-1 mt-0 cursor-grab touch-none select-none p-1 text-gray-300 transition hover:text-gray-500 active:cursor-grabbing"
        title="Déplacer la tâche"
        aria-label="Déplacer la tâche"
        @pointerdown.stop="emits('grab', $event)"
        @click.stop
      >
        <GripVertical class="size-4" />
      </button>
      <p class="flex-1 text-xs font-semibold leading-snug text-gray-900">
        {{ task.title }}
      </p>
      <span
        class="shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-semibold"
        :class="priority.chipClass"
      >
        {{ priority.label }}
      </span>
    </div>

    <div class="mt-2 flex items-center gap-2">
      <template v-if="task.assignee_ids.length">
        <div class="flex -space-x-1.5">
          <span
            v-for="assignee in assignees.slice(0, 3)"
            :key="assignee.id"
            class="flex size-5 items-center justify-center rounded-full border border-white bg-primary/10 text-[9px] font-bold text-primary"
            :title="assignee.user?.name ?? ''"
          >
            {{ initialsOf(assignee.user?.name) }}
          </span>
        </div>
        <span class="truncate text-[10px] text-gray-500">{{ assigneeLabel }}</span>
        <span v-if="dueLabel" class="ml-auto shrink-0 text-[10px] font-medium text-gray-500">
          {{ dueLabel }}
        </span>
      </template>
      <template v-else>
        <span class="rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
          Non assignée
        </span>
        <button
          class="ml-auto flex items-center gap-1 rounded-md border border-gray-200 px-1.5 py-0.5 text-[10px] font-semibold text-primary hover:border-primary/40"
          @click.stop="emits('claim')"
        >
          <UserPlus class="size-3" />
          Se l'assigner
        </button>
      </template>
    </div>

    <div class="mt-2 flex items-center gap-2 border-t border-gray-100 pt-2">
      <span v-if="task.category" class="rounded-md bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600">
        {{ task.category }}
      </span>
      <span v-if="task.comments_count" class="flex items-center gap-1 text-[10px] text-gray-400">
        <MessageSquare class="size-3" />
        {{ task.comments_count }}
      </span>
      <div class="ml-auto flex items-center gap-0.5">
        <button
          class="flex size-5 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent"
          title="Reculer le statut"
          :disabled="!canRecede"
          @click.stop="emits('recede')"
        >
          <ChevronLeft class="size-3.5" />
        </button>
        <button
          class="flex size-5 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent"
          title="Avancer le statut"
          :disabled="!canAdvance"
          @click.stop="emits('advance')"
        >
          <ChevronRight class="size-3.5" />
        </button>
      </div>
    </div>
  </article>
</template>
